import React, { useState, useEffect } from 'react';
import { NavLink, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { Menu, X, Languages, ArrowRight } from 'lucide-react';
import { useLang } from '../i18n/LangContext';

const Navbar = () => {
  const { t, lang, toggleLang, isRTL } = useLang();
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 30);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  const links = [
    { to: '/', label: t('nav.home'), testid: 'nav-home' },
    { to: '/services', label: t('nav.services'), testid: 'nav-services' },
    { to: '/portfolio', label: t('nav.portfolio'), testid: 'nav-portfolio' },
    { to: '/about', label: t('nav.about'), testid: 'nav-about' },
    { to: '/contact', label: t('nav.contact'), testid: 'nav-contact' },
  ];

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
      className={`fixed top-0 inset-x-0 z-50 transition-all duration-500 ${
        scrolled ? 'glass border-b border-white/10' : 'bg-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12 h-20 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2 group">
          <motion.div
            whileHover={{ rotate: 90 }}
            transition={{ type: 'spring', stiffness: 200, damping: 12 }}
            className="w-9 h-9 rounded-md bg-blue-600 flex items-center justify-center"
          >
            <span className="heading-font font-black text-white text-lg">L</span>
          </motion.div>
          <span className="heading-font font-extrabold text-white text-xl tracking-tight">
            lyomas<span className="text-blue-500">tech</span>
          </span>
        </Link>

        <nav className="hidden lg:flex items-center gap-1">
          {links.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={l.testid}
              className={({ isActive }) =>
                `relative px-4 py-2 text-sm font-medium transition-colors ${
                  isActive ? 'text-white' : 'text-zinc-400 hover:text-white'
                }`
              }
            >
              {({ isActive }) => (
                <>
                  <span>{l.label}</span>
                  {isActive && (
                    <motion.span
                      layoutId="nav-underline"
                      className="absolute inset-x-3 -bottom-0.5 h-px bg-blue-500"
                      transition={{ type: 'spring', stiffness: 350, damping: 30 }}
                    />
                  )}
                </>
              )}
            </NavLink>
          ))}
        </nav>

        <div className="flex items-center gap-2">
          <button
            onClick={toggleLang}
            data-testid="lang-toggle"
            className="hidden sm:flex items-center gap-2 h-10 px-3 rounded-md border border-white/10 hover:border-white/30 transition text-sm text-zinc-300"
            aria-label="Toggle language"
          >
            <Languages className="w-4 h-4" />
            <span className="font-semibold">{lang === 'en' ? 'AR' : 'EN'}</span>
          </button>

          <Link
            to="/contact"
            data-testid="nav-cta"
            className="hidden md:inline-flex items-center gap-2 h-10 px-4 rounded-md bg-blue-600 hover:bg-blue-500 transition text-sm font-semibold text-white"
          >
            {t('nav.cta')}
            <ArrowRight className={`w-4 h-4 ${isRTL ? 'rtl-flip' : ''}`} />
          </Link>

          <button
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden w-10 h-10 grid place-items-center rounded-md border border-white/10"
            aria-label="Menu"
          >
            {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: 'auto', opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="lg:hidden overflow-hidden glass border-t border-white/10"
            data-testid="mobile-menu"
          >
            <div className="px-6 py-6 flex flex-col gap-1">
              {links.map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  data-testid={`mobile-${l.testid}`}
                  className={({ isActive }) =>
                    `px-4 py-3 rounded-md text-base font-medium ${
                      isActive ? 'bg-white/5 text-white' : 'text-zinc-300 hover:bg-white/5'
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={toggleLang}
                data-testid="mobile-lang-toggle"
                className="mt-2 flex items-center gap-2 px-4 py-3 rounded-md border border-white/10 text-zinc-300"
              >
                <Languages className="w-4 h-4" />
                {lang === 'en' ? 'Switch to Arabic' : 'التبديل للإنجليزية'}
              </button>
              <Link
                to="/contact"
                data-testid="mobile-nav-cta"
                className="mt-2 inline-flex items-center justify-center gap-2 h-12 rounded-md bg-blue-600 text-white font-semibold"
              >
                {t('nav.cta')} <ArrowRight className={`w-4 h-4 ${isRTL ? 'rtl-flip' : ''}`} />
              </Link>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
};

export default Navbar;
